import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, formatCOP } from '../services/api';
import { useVendor } from '../context/VendorContext';
import { loadOfflineQueue } from '../utils/offlinePedidoQueue';
import { pedidoClienteLabel } from '../utils/textEncoding';

function ymdBogota(value = new Date()) {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return '';
  }
}

function hoyBogota() {
  return ymdBogota(new Date());
}

function formatFecha(iso) {
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatHora(iso) {
  return new Date(iso).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
}

export default function Pedidos({ refreshKey, onSelect, onOpenLocal }) {
  const { vendedor } = useVendor();
  const [pedidos, setPedidos] = useState([]);
  const [offlineRows, setOfflineRows] = useState(() => loadOfflineQueue(vendedor?.id));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filtro, setFiltro] = useState('hoy'); // hoy | todos | YYYY-MM-DD

  const refreshOffline = useCallback(() => {
    setOfflineRows(loadOfflineQueue(vendedor?.id));
  }, [vendedor?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    refreshOffline();
    try {
      const res = await api.getPedidos('');
      setPedidos(res.data || []);
    } catch (err) {
      setPedidos([]);
      setLoadError(err.message || 'No se pudieron cargar los pedidos');
    } finally {
      setLoading(false);
    }
  }, [refreshKey, refreshOffline]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onOnline = () => refreshOffline();
    const onQueueChanged = () => refreshOffline();
    window.addEventListener('online', onOnline);
    window.addEventListener('storage', refreshOffline);
    window.addEventListener('oh-offline-queue-changed', onQueueChanged);
    const t = setInterval(refreshOffline, 8000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('storage', refreshOffline);
      window.removeEventListener('oh-offline-queue-changed', onQueueChanged);
      clearInterval(t);
    };
  }, [refreshOffline]);

  const dayKey = filtro === 'hoy' ? hoyBogota() : filtro === 'todos' ? null : filtro;

  const offlineVisibles = useMemo(() => {
    if (!dayKey) return offlineRows;
    return offlineRows.filter((r) => ymdBogota(r.created_at) === dayKey);
  }, [offlineRows, dayKey]);

  const pedidosVisibles = useMemo(() => {
    const base = pedidos.filter((p) => p.estado !== 'cancelado');
    if (!dayKey) return base;
    return base.filter((p) => ymdBogota(p.created_at) === dayKey);
  }, [pedidos, dayKey]);

  const totales = useMemo(() => {
    const offlineSum = offlineVisibles.reduce((s, r) => s + (Number(r.subtotal) || 0), 0);
    const offlineItems = offlineVisibles.reduce((s, r) => s + (Number(r.items_count) || 0), 0);
    const enviadosSum = pedidosVisibles.reduce((s, p) => s + (Number(p.subtotal) || 0), 0);
    const enviadosItems = pedidosVisibles.reduce((s, p) => s + (Number(p.items_count) || 0), 0);
    return {
      enviadosCount: pedidosVisibles.length,
      enviadosPlata: enviadosSum,
      offlineCount: offlineVisibles.length,
      offlinePlata: offlineSum,
      pedidosCount: pedidosVisibles.length + offlineVisibles.length,
      items: enviadosItems + offlineItems,
      plata: enviadosSum + offlineSum,
    };
  }, [pedidosVisibles, offlineVisibles]);

  const showOffline = offlineVisibles.length > 0;
  const empty = !loading && !pedidosVisibles.length && !showOffline;
  const filtroLabel =
    filtro === 'hoy' ? 'hoy' : filtro === 'todos' ? 'todos' : formatFecha(`${filtro}T12:00:00`);

  return (
    <section className="page pedidos-page-v2">
      <header className="pedidos-head">
        <div>
          <h1>Mis pedidos</h1>
          <p>
            {totales.pedidosCount} pedido{totales.pedidosCount === 1 ? '' : 's'}
            {filtro !== 'todos' ? ` · ${filtroLabel}` : ''}
            {totales.items ? ` · ${totales.items} und.` : ''}
            {totales.offlineCount ? ` · ${totales.offlineCount} por enviar` : ''}
          </p>
          <div className="pedidos-filtros-fecha" role="group" aria-label="Filtrar por fecha">
            <button
              type="button"
              className={`pedidos-filtro-chip${filtro === 'hoy' ? ' active' : ''}`}
              onClick={() => setFiltro('hoy')}
            >
              Hoy
            </button>
            <button
              type="button"
              className={`pedidos-filtro-chip${filtro === 'todos' ? ' active' : ''}`}
              onClick={() => setFiltro('todos')}
            >
              Todos
            </button>
            <label className="pedidos-filtro-fecha">
              <span className="sr-only">Fecha</span>
              <input
                type="date"
                value={filtro !== 'hoy' && filtro !== 'todos' ? filtro : ''}
                max={hoyBogota()}
                onChange={(e) => {
                  const v = e.target.value;
                  setFiltro(v || 'hoy');
                }}
              />
            </label>
          </div>
        </div>
        {!loading && !empty ? (
          <div className="pedidos-gran-total" title="Suma fila por fila del filtro actual">
            <span>Total {filtro === 'hoy' ? 'hoy' : filtro === 'todos' ? 'en lista' : filtroLabel}</span>
            <strong>{formatCOP(totales.plata)}</strong>
            <em className="pedidos-gran-total-sub">
              {totales.enviadosCount} enviado{totales.enviadosCount === 1 ? '' : 's'}{' '}
              {formatCOP(totales.enviadosPlata)}
              {totales.offlineCount
                ? ` + ${totales.offlineCount} por enviar ${formatCOP(totales.offlinePlata)}`
                : ''}
            </em>
          </div>
        ) : null}
      </header>

      {loadError ? (
        <p className="error banner">
          {loadError}{' '}
          <button type="button" className="link-btn" onClick={load}>
            Reintentar
          </button>
        </p>
      ) : null}

      {loading ? (
        <p className="loading-grid">Cargando pedidos...</p>
      ) : empty ? (
        <div className="pedidos-empty">
          <p>{filtro === 'hoy' ? 'No hay pedidos de hoy' : 'No hay pedidos en este filtro'}</p>
          <span>
            {filtro !== 'todos' ? (
              <button type="button" className="link-btn" onClick={() => setFiltro('todos')}>
                Ver todos
              </button>
            ) : (
              'Crea uno desde Nuevo pedido'
            )}
          </span>
        </div>
      ) : (
        <div className="pedidos-table pedidos-table-no-estado">
          <div className="pedidos-table-head">
            <span>Pedido</span>
            <span>Cliente</span>
            <span>Fecha</span>
            <span>Total</span>
          </div>
          {showOffline
            ? offlineVisibles.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className="pedidos-table-row pedidos-offline-row"
                  onClick={() => onOpenLocal?.(row)}
                >
                  <span className="pedidos-col-numero">
                    <strong>Local</strong>
                    <em>{formatHora(row.created_at)}</em>
                  </span>
                  <span className="pedidos-col-cliente">
                    <strong>{row.cliente_nombre}</strong>
                    <em>
                      {row.items_count} und. · por enviar · tocar para abrir
                    </em>
                  </span>
                  <span className="pedidos-col-fecha">{formatFecha(row.created_at)}</span>
                  <span className="pedidos-col-total">{formatCOP(row.subtotal)}</span>
                </button>
              ))
            : null}
          {pedidosVisibles.map((p) => (
            <button
              key={p.id}
              type="button"
              className="pedidos-table-row"
              onClick={() => onSelect(p.id)}
            >
              <span className="pedidos-col-numero">
                <strong>{p.numero}</strong>
                <em>{formatHora(p.created_at)}</em>
              </span>
              <span className="pedidos-col-cliente">
                <strong>{pedidoClienteLabel(p)}</strong>
                {p.es_sede || p.padre_codigo ? (
                  <em className="cliente-sede-badge">Sucursal</em>
                ) : null}
                <em>
                  {Number(p.items_count) || 0} und.
                  {p.cliente_telefono ? ` · ${p.cliente_telefono}` : ''}
                </em>
              </span>
              <span className="pedidos-col-fecha">{formatFecha(p.created_at)}</span>
              <span className="pedidos-col-total">{formatCOP(p.subtotal)}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
